// @flow
import type moment from 'moment'
import client from '../../client'
import { config } from '../../config'
import prebookSearchTripBuilder from './builders/prebookSearchTrip'
import ondemandSearchTripBuilder from './builders/ondemandSearchTrip'
import format from '../../utils/format'
import { poller } from '../../utils/poller'
import {
  filterSearchQuery,
  isMoreComing,
  isResultEmpty,
  isOndemandResultEmpty,
  resolvePlaces,
} from './utils'

// Types
import type { Trip } from '../../models/trip'
import type {
  OndemandSearchResponse,
  PrebookSearchResponse,
} from '../../api_types'

export type Options = {
  session_id: string,
  trip_id: string,
  ondemand?: boolean,
  params: {
    currency?: string,
    pickup_datetime?: moment,
    flight_datetime?: moment,
  },
}

// API Endpoints
export const PREBOOK_POOL_ENDPOINT = '/search/{0}/poll/'
export const PREBOOK_TRIP_GET_ENDPOINT = '/search/{0}/{1}/'
export const ONDEMAND_POOL_ENDPOINT = '/ondemand/search/{0}/poll/'
export const ONDEMAND_TRIP_GET_ENDPOINT = '/ondemand/search/research/{0}/{1}/'

/**
 * Updates trip object by doing internal backend research
 * @param {Object} data Research information
 * @param {string} data.session_id Id of search that was used to get this trip
 * @param {string} data.trip_id Id of trip from search results
 * @param {string} data.params search params
 */
export const researchPrebook = (data: Options): Promise<?Trip> => {
  const { session_id, trip_id, params } = data
  const query = filterSearchQuery(params)

  return poller({
    initPoll: async () => {
      const fullQuery = await resolvePlaces(query)
      const endpoint = format(PREBOOK_TRIP_GET_ENDPOINT, session_id, trip_id)
      return client.patch(endpoint, {
        body: fullQuery,
        baseUrl: config.BASE_URL_SEARCH,
      })
    },
    pollNext: (id) =>
      client.get(format(PREBOOK_POOL_ENDPOINT, id), {
        baseUrl: config.BASE_URL_SEARCH,
      }),
    builder: (response: PrebookSearchResponse) =>
      prebookSearchTripBuilder(response.results[0], response),
    isMoreComing,
    isResultEmpty,
    pollAll: true,
  })().then((results: ?Array<Trip>) =>
    results && results.length > 0 ? results[0] : null
  )
}

export const researchOndemand = (data: Options): Promise<?Trip> => {
  const { session_id, trip_id, params } = data
  const query = filterSearchQuery(params)

  return poller({
    initPoll: async () => {
      const fullQuery = await resolvePlaces(query)
      const endpoint = format(ONDEMAND_TRIP_GET_ENDPOINT, session_id, trip_id)
      return client.patch(endpoint, { body: fullQuery })
    },
    pollNext: (id) => client.get(format(ONDEMAND_POOL_ENDPOINT, id)),
    builder: (response: OndemandSearchResponse) =>
      ondemandSearchTripBuilder(response.ride_types[0].rides[0], response),
    isMoreComing,
    isResultEmpty: isOndemandResultEmpty,
    pollAll: true,
  })().then((results: ?Array<Trip>) =>
    results && results.length > 0 ? results[0] : null
  )
}

const research = (data: Options): Promise<?Trip> =>
  data.ondemand ? researchOndemand(data) : researchPrebook(data)

export default research
